import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Category, InterfaceType } from '../../types';
import { getPatternsByCategory } from '../../data';
import { categoryColor, slugToCategory, categoryDescriptions } from '../../utils/categoryColors';
import { PatternCard } from '../../components/PatternCard/PatternCard';
import { useReveal } from '../../hooks/useReveal';
import styles from './CategoryPage.module.css';

const INTERFACE_FILTERS: Array<InterfaceType | 'All'> = ['All', 'Chat UI', 'Voice UI', 'Agentic UI'];

export function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? slugToCategory[slug] : undefined;
  const [ifFilter, setIfFilter] = useState<InterfaceType | 'All'>('All');
  useReveal([slug, ifFilter]);

  if (!category) {
    return (
      <main className={styles.main}>
        <div className="container">
          <div className={styles.notFound}>
            <h1>Category not found</h1>
            <Link to="/" className={styles.back}>← Back to home</Link>
          </div>
        </div>
      </main>
    );
  }

  const categoryPatterns = getPatternsByCategory(category);
  const color = categoryColor[category as Category];

  const visiblePatterns = ifFilter === 'All'
    ? categoryPatterns
    : categoryPatterns.filter(p => p.interface_type?.includes(ifFilter));

  return (
    <main className={styles.main}>
      <div className={styles.hero}>
        <div className="container">
          <Link to="/" className={styles.breadcrumb}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            All categories
          </Link>
          <div className={styles.heroInner}>
            <div className={styles.categoryPill} style={{ color, borderColor: `${color}40`, background: `${color}10` }}>
              {category}
            </div>
            <h1 className={`${styles.title} rise`}>{category} Patterns</h1>
            <p className={`${styles.desc} page-intro`}>{categoryDescriptions[category as Category]}</p>
            <p className={styles.count} style={{ color }}>
              {categoryPatterns.length} {categoryPatterns.length === 1 ? 'pattern' : 'patterns'}
            </p>
          </div>
        </div>
      </div>

      <div className="container">
        <div className={styles.toolbar}>
          <div className={styles.filters}>
            {INTERFACE_FILTERS.map((f) => (
              <button
                key={f}
                className={`${styles.filterBtn} ${ifFilter === f ? styles.filterBtnActive : ''}`}
                onClick={() => setIfFilter(f)}
              >
                {f}
              </button>
            ))}
          </div>
          {ifFilter !== 'All' && (
            <span className={styles.filterCount}>
              {visiblePatterns.length} of {categoryPatterns.length}
            </span>
          )}
        </div>

        <div className={styles.grid}>
          {visiblePatterns.length === 0 ? (
            <div className={styles.empty}>
              <p>No {ifFilter} patterns in this category.</p>
              <button className={styles.clearFilter} onClick={() => setIfFilter('All')}>Show all</button>
            </div>
          ) : (
            visiblePatterns.map((p, i) => <PatternCard key={p.id} pattern={p} index={i} />)
          )}
        </div>
      </div>
    </main>
  );
}
